import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Ejecutamos la geolocalización dinámica respetando la arquitectura nativa
    this.initGeoLocalization();

    this.play();
  }

  /**
   * Detecta la IP del usuario de manera asíncrona (<900ms) y actualiza los banners dinámicos
   */
  initGeoLocalization() {
    const geoBanners = this.querySelectorAll('[data-geo-banner="true"]');
    if (geoBanners.length === 0) return;

    const grupo1Ciudades = ['medellin', 'envigado', 'sabaneta', 'itagui', 'itaguí'];
    
    // AbortController para forzar la velocidad del fallback si tarda más de 900ms
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 900);

    fetch('https://ipapi.co/json/', { signal: controller.signal })
      .then(response => response.json())
      .then(data => {
        clearTimeout(timeoutId);

        if (!data || !data.city) throw new Error('Datos de IP ausentes');

        const ciudadUsuario = data.city.toLowerCase().trim();
        const paisUsuario = data.country_code ? data.country_code.toUpperCase() : '';
        let targetGroup = 'g1'; // Por defecto Medellín

        if (paisUsuario !== 'CO') {
          targetGroup = 'g3'; // Extranjero -> Resto del mundo/país
        } else if (grupo1Ciudades.some(ciudad => ciudadUsuario.includes(ciudad))) {
          targetGroup = 'g1'; // AMVA
        } else if (ciudadUsuario.includes('bogota') || ciudadUsuario.includes('bogotá')) {
          targetGroup = 'g2'; // Bogotá
        } else {
          targetGroup = 'g3'; // Resto de Colombia
        }

        // Aplicamos los cambios a cada bloque marcado como geo-banner de manera limpia
        geoBanners.forEach(banner => {
          const msg = banner.getAttribute(`data-msg-${targetGroup}`);
          const url = banner.getAttribute(`data-url-${targetGroup}`);
          
          const textElem = banner.querySelector('.announcement-bar__text');
          const linkElem = banner.querySelector('.announcement-bar__link');
          const hiddenTextElem = banner.querySelector('.visually-hidden');

          if (textElem && msg) textElem.innerHTML = msg;
          if (hiddenTextElem && msg) hiddenTextElem.innerText = msg;
          if (linkElem && url) linkElem.setAttribute('href', url);
        });
      })
      .catch(err => {
        // Fallback inmediato en caso de error, lentitud o adblockers que bloqueen ipapi.co
        console.warn('Geolocalización gestionada por fallback por defecto (Medellín/AM):', err.message);
        geoBanners.forEach(banner => {
          const msg = banner.getAttribute('data-msg-g1');
          const url = banner.getAttribute('data-url-g1');
          
          const textElem = banner.querySelector('.announcement-bar__text');
          const linkElem = banner.querySelector('.announcement-bar__link');

          if (textElem && msg) textElem.innerHTML = msg;
          if (linkElem && url) linkElem.setAttribute('href', url);
        });
      });
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}